import {
  explodeSchedule,
} from 'common/benefitSchedule';
import {Plus, X} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import {formatPeriodLabel} from 'src/CardBenefitsView/benefitUtils';
import {TextInput} from 'src/inputs/TextInput';
import type {
  BenefitCadence,
  BenefitSchedule,
  PeriodSeed} from 'common/benefitSchedule';

/**
 * Editor for a benefit's regular schedule. The schedule is never stored —
 * on save the modal explodes it into period rows (the DB's only format);
 * on edit the modal recovers it from the rows via inferSchedule.
 */

export type ScheduleFormState = {
  cadence: BenefitCadence;
  calendarAligned: boolean;
  amount: string; // dollars
  /** Periods with a different amount, keyed by period startDate */
  exceptions: Array<{startDate: number; amount: string}>;
};

export const defaultScheduleState: ScheduleFormState = {
  cadence: 'monthly',
  calendarAligned: false,
  amount: '',
  exceptions: [],
};

const centsToDollars = (cents: number): string => (cents / 100).toFixed(2);

export const scheduleStateFromInferred = (
  schedule: BenefitSchedule,
): ScheduleFormState => ({
  cadence: schedule.cadence,
  calendarAligned: schedule.calendarAligned,
  amount: centsToDollars(schedule.amountPerPeriod),
  exceptions: schedule.exceptions.map((e) => ({
    startDate: e.startDate,
    amount: centsToDollars(e.amount),
  })),
});

const parseCents = (dollars: string, label: string): number => {
  const cents = Math.round(parseFloat(dollars) * 100);
  if (isNaN(cents) || cents <= 0) {
    throw new Error(`${label} must be positive`);
  }
  return cents;
};

/** Explode the form state into period rows over the given bounds. Throws on
 * invalid amounts. */
export const explodeScheduleState = (
  state: ScheduleFormState,
  bounds: {startDate: number; endDate: number},
): PeriodSeed[] => {
  const starts = state.exceptions.map((e) => e.startDate);
  if (new Set(starts).size !== starts.length) {
    throw new Error('Each period can only have one exception');
  }
  return explodeSchedule({
    cadence: state.cadence,
    calendarAligned: state.calendarAligned,
    amountPerPeriod: parseCents(state.amount, 'Amount'),
    startDate: bounds.startDate,
    endDate: bounds.endDate,
    exceptions: state.exceptions.map((e) => ({
      startDate: e.startDate,
      amount: parseCents(e.amount, 'Exception amount'),
    })),
  });
};

const CADENCE_OPTIONS: Array<{value: BenefitCadence; label: string}> = [
  {value: 'monthly', label: 'Monthly'},
  {value: 'quarterly', label: 'Quarterly'},
  {value: 'semi_annual', label: 'Semi-Annual'},
  {value: 'annual', label: 'Annual'},
];

type Props = {
  value: ScheduleFormState;
  onChange: (value: ScheduleFormState) => void;
  /** Coverage bounds for the schedule — the fee period dates. */
  bounds: {startDate: number; endDate: number};
};

export const ScheduleEditor = ({value, onChange, bounds}: Props) => {
  const set = <K extends keyof ScheduleFormState>(
    key: K,
    val: ScheduleFormState[K],
  ) => onChange({...value, [key]: val});

  // Preview periods for the exception pickers (amounts are irrelevant here)
  const periodOptions = React.useMemo(
    () =>
      explodeSchedule({
        cadence: value.cadence,
        calendarAligned: value.calendarAligned,
        amountPerPeriod: 0,
        startDate: bounds.startDate,
        endDate: bounds.endDate,
      }).map((p) => ({
        startDate: p.startDate,
        label: formatPeriodLabel(p.startDate, p.endDate),
      })),
    [value.cadence, value.calendarAligned, bounds.startDate, bounds.endDate],
  );

  const handleAddException = () => {
    const taken = new Set(value.exceptions.map((e) => e.startDate));
    const firstFree = periodOptions.find((p) => !taken.has(p.startDate));
    if (!firstFree) return;
    set('exceptions', [
      ...value.exceptions,
      {startDate: firstFree.startDate, amount: ''},
    ]);
  };

  const handleExceptionChange = (
    index: number,
    change: Partial<{startDate: number; amount: string}>,
  ) => {
    const updated = [...value.exceptions];
    updated[index] = {...updated[index], ...change};
    set('exceptions', updated);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium">Frequency</label>
          <select
            className="border-input bg-background rounded-md border px-3 py-1.5 text-sm"
            value={value.cadence}
            onChange={(e) => {
              const match = CADENCE_OPTIONS.find(
                (o) => o.value === e.target.value,
              );
              if (match) {
                // Changing the frequency invalidates period-keyed exceptions
                onChange({...value, cadence: match.value, exceptions: []});
              }
            }}
          >
            {CADENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-xs font-medium">Amount per period</label>
          <div className="relative">
            <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
              $
            </span>
            <TextInput
              value={value.amount}
              onChange={(v) => set('amount', v)}
              placeholder="0.00"
              className="pl-7"
            />
          </div>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.calendarAligned}
          onChange={(e) => {
            // Realigning moves period boundaries, so period-keyed
            // exceptions no longer apply
            onChange({
              ...value,
              calendarAligned: e.target.checked,
              exceptions: [],
            });
          }}
        />
        Align to calendar (e.g. monthly periods reset on the 1st)
      </label>

      <div className="flex flex-col gap-2">
        {value.exceptions.map((exception, index) => {
          // Each period can carry only one exception — hide periods already
          // taken by other rows (explodeSchedule would drop the duplicate)
          const takenByOthers = new Set(
            value.exceptions
              .filter((_, i) => i !== index)
              .map((e) => e.startDate),
          );
          return (
          <div key={index} className="flex items-center gap-2">
            <select
              className="border-input bg-background w-40 rounded-md border px-3 py-1.5 text-sm"
              value={exception.startDate}
              onChange={(e) =>
                handleExceptionChange(index, {
                  startDate: parseInt(e.target.value, 10),
                })
              }
            >
              {periodOptions
                .filter((p) => !takenByOthers.has(p.startDate))
                .map((p) => (
                  <option key={p.startDate} value={p.startDate}>
                    {p.label}
                  </option>
                ))}
            </select>
            <div className="relative flex-1">
              <span className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm">
                $
              </span>
              <TextInput
                value={exception.amount}
                onChange={(v) => handleExceptionChange(index, {amount: v})}
                placeholder="0.00"
                className="pl-7"
              />
            </div>
            <Button
              size="icon"
              variant="ghost"
              type="button"
              onClick={() =>
                set(
                  'exceptions',
                  value.exceptions.filter((_, i) => i !== index),
                )
              }
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          );
        })}
        {value.exceptions.length < periodOptions.length && (
          <Button
            variant="ghost"
            type="button"
            className="text-muted-foreground self-start text-sm"
            onClick={handleAddException}
          >
            <Plus className="mr-1 h-4 w-4" />
            Different amount in a specific period
          </Button>
        )}
      </div>
    </div>
  );
};
